import clsx from 'clsx';
import React from 'react';

export interface ButtonGroupOption<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

export interface ButtonGroupProps<T extends string = string> {
  options: ButtonGroupOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Segmented-control toggle for a small exclusive choice with short labels
 * (e.g. Billing Type, Route status filter). Supports 2-4 options.
 */
const ButtonGroup = <T extends string = string>({
  options,
  value,
  onChange,
  disabled,
  className,
}: ButtonGroupProps<T>) => (
  <div
    role="radiogroup"
    className={clsx(
      'grid gap-2 rounded-[14px] bg-regular p-1.5',
      disabled && 'pointer-events-none opacity-50',
      className
    )}
    style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
  >
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={active}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={clsx(
            'flex items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-transparent py-[11px] px-3 text-sm font-semibold transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2',
            '[&>svg]:h-[17px] [&>svg]:w-[17px]',
            active
              ? 'bg-white text-primary shadow-[0_2px_8px_rgba(0,29,47,0.08)]'
              : 'text-textLight hover:text-textDark'
          )}
        >
          {option.icon}
          {option.label}
        </button>
      );
    })}
  </div>
);

export default ButtonGroup;
